<script setup>
// SiteUp (W4/M1+M2): cartão de enquete do WhatsApp.
//
// A enquete chega no fio como texto — o app do WAHA converte a mensagem
// estruturada do WhatsApp numa lista de opções. O backend grava
// `content_attributes.poll` na CHEGADA (Waha::PollAttributeAttach) e agrega
// os votos depois (Waha::PollVoteIngest).
//
// Aqui só desenhamos. Nada é parseado no front: pergunta, opções e modo
// vêm prontos do backend, para não existirem duas implementações do parse.
//
// Dois estados (mockup M1/M2):
//   - sem voto → círculos vazios + "Ninguém votou ainda"
//   - com voto → barra proporcional à opção mais votada + "N pessoas votaram"
// O cartão NÃO é clicável: quem vota é o cliente, no WhatsApp.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';
import { buildPollResults, countVoters } from 'dashboard/helper/pollResults';

const { contentAttributes } = useMessageContext();
const { t } = useI18n();

const poll = computed(() => contentAttributes.value?.poll ?? {});

const question = computed(() => poll.value.question || '');

const voterCount = computed(() => countVoters(poll.value));

const hasVotes = computed(() => voterCount.value > 0);

const results = computed(() => buildPollResults(poll.value));

// `multiple` só existe quando o backend teve fonte confiável. Sem ela o
// rótulo fica só "Enquete" — não inventamos "escolha única".
const title = computed(() => {
  if (poll.value.multiple === true)
    return t('CONVERSATION.POLL.TITLE_MULTIPLE');
  if (poll.value.multiple === false) return t('CONVERSATION.POLL.TITLE_SINGLE');
  return t('CONVERSATION.POLL.TITLE');
});
</script>

<template>
  <BaseBubble class="p-3">
    <div class="flex flex-col gap-2 min-w-[15rem]">
      <!-- Cores por HERANÇA + opacidade, nunca slate fixo: a bolha de saída é
           azul com texto branco (variant agent) e a de entrada é cinza com
           texto escuro — cor fixa de tema neutro fica ilegível no azul, como o
           screenshot do preview provou (#438). -->
      <div class="flex items-center gap-1.5 opacity-80">
        <Icon icon="i-ph-chart-bar" class="size-4" />
        <span class="text-xs font-medium uppercase tracking-wide">
          {{ title }}
        </span>
      </div>

      <p v-if="question" class="text-sm font-medium">
        {{ question }}
      </p>

      <!-- M1: sem voto — círculos vazios. Não mostra barra nem contagem. -->
      <ul v-if="!hasVotes" class="flex flex-col gap-2 m-0 p-0 list-none">
        <li
          v-for="result in results"
          :key="result.option"
          class="flex items-center gap-2"
        >
          <span
            aria-hidden="true"
            class="size-3.5 shrink-0 rounded-full border border-current opacity-60"
          />
          <span class="text-sm opacity-80">
            {{ result.option }}
          </span>
        </li>
      </ul>

      <!-- M2: com voto — contagem + barra (proporcional à MAIS votada). -->
      <ul v-else class="flex flex-col gap-2 m-0 p-0 list-none">
        <li
          v-for="result in results"
          :key="result.option"
          class="flex flex-col gap-1"
        >
          <div class="flex items-baseline justify-between gap-2">
            <span
              class="text-sm"
              :class="result.leading ? 'font-medium' : 'opacity-80'"
            >
              {{ result.option }}
            </span>
            <span class="text-xs tabular-nums opacity-80">
              {{ result.count }}
            </span>
          </div>
          <!-- A barra é redundante de propósito: a contagem ao lado já carrega
               a informação, então ela sai da árvore de acessibilidade em vez de
               repetir o número para quem usa leitor de tela. -->
          <div
            aria-hidden="true"
            class="h-1.5 rounded-full bg-current/15 overflow-hidden"
          >
            <div
              class="h-full rounded-full bg-current transition-[width] duration-300 motion-reduce:transition-none"
              :class="result.leading ? '' : 'opacity-40'"
              :style="{ width: `${result.percentage}%` }"
            />
          </div>
        </li>
      </ul>

      <span class="text-xs opacity-70">
        {{ t('CONVERSATION.POLL.VOTERS', { count: voterCount }) }}
      </span>
    </div>
  </BaseBubble>
</template>
